import {
  STORE_KEY_ID_LIST,
  STORE_KEY_LOGIN_TYPE,
  evalCondition,
  storeGet,
} from "mailup-react-helpers";

import {
  EMAIL_OPTOUT_TYPE_BLOCKEDEMAIL,
  EMAIL_OPTOUT_TYPE_BLOCKEDEMAILCONFIGMTA,
  EMAIL_OPTOUT_TYPE_BLOCKEDEMAILMTA,
  EMAIL_OPTOUT_TYPE_BLOCKEDEMAILSPAM,
  EMAIL_OPTOUT_TYPE_BOUNCE,
  EMAIL_OPTOUT_TYPE_CASELLAABBANDONATA,
  EMAIL_OPTOUT_TYPE_CONSOLE,
  EMAIL_OPTOUT_TYPE_CONTINUALBOUNCES,
  EMAIL_OPTOUT_TYPE_DEFINITIVECONTBOUNCES,
  EMAIL_OPTOUT_TYPE_DOMINIOERRATO,
  EMAIL_OPTOUT_TYPE_EMAILERRATA,
  EMAIL_OPTOUT_TYPE_FEEDBACKLOOP,
  EMAIL_OPTOUT_TYPE_IMPORT,
  EMAIL_OPTOUT_TYPE_LISTUNSUBSCRIBEMAILTO,
  EMAIL_OPTOUT_TYPE_LISTUNSUBSCRIBEPAGE,
  EMAIL_OPTOUT_TYPE_PROVISIONALCONTBOUNCES,
  EMAIL_OPTOUT_TYPE_RECIPIENTINACTIVE,
  EMAIL_OPTOUT_TYPE_RIGHTTOBEFORGOTTEN,
  EMAIL_OPTOUT_TYPE_UNSUBSCRIBE,
  LOGIN_TYPE_WEBLOGIN,
} from "Constants";

import {
  type ColumnName,
  type GroupsPreferences,
  type ModalBulkUnsubscribePreferences,
  type ModalUnsubscribePreferences,
  type PageParams,
  type PagePreferences,
  type SearchFilters,
  type SearchFiltersState,
  defaultPagePreferences,
} from "Pages/RecipientsList/model";

import type {
  SubscriptionSpecOptOutType,
  SubscriptionStatus,
} from "Types/beRecipientsApi";

import {
  beAccountSettingsApiGetUserSetting,
  beAccountSettingsApiUpsertUserSettings,
} from "Api/beAccountSettingsApi";

export const safeColumns = (columns: ColumnName[]): ColumnName[] => {
  const hasEngagement: boolean = evalCondition("co:DELSTATS OR co:ENGAGEMENT");

  //  do not export Engagement if hasn't option
  return columns.filter((c) => !(c === "Engagement" && !hasEngagement));
};

export const fetchPagePreferences = async (): Promise<PagePreferences> => {
  const idList = storeGet(STORE_KEY_ID_LIST);

  const usersettings = await beAccountSettingsApiGetUserSetting(
    idList,
    "recipients-list-filters",
  );

  if (usersettings === null) {
    return {
      ...defaultPagePreferences,
      columns: safeColumns(defaultPagePreferences.columns),
    };
  }

  try {
    const result = JSON.parse(usersettings.DataValue);

    const tmp = { ...defaultPagePreferences, ...result };

    return {
      ...tmp,
      columns: safeColumns(tmp.columns),
    };
  } catch (e) {}

  return {
    ...defaultPagePreferences,
    columns: safeColumns(defaultPagePreferences.columns),
  };
};

export const updatePagePreferences = async ({
  status,
  columns,
  modalUnsubscribe,
  modalBulkUnsubscribe,
  groups,
}: {
  status?: SearchFilters["status"];
  columns?: ColumnName[];
  modalUnsubscribe?: ModalUnsubscribePreferences;
  modalBulkUnsubscribe?: ModalBulkUnsubscribePreferences;
  groups?: GroupsPreferences;
}): Promise<void> => {
  const loginType = storeGet(STORE_KEY_LOGIN_TYPE);
  const idList = storeGet(STORE_KEY_ID_LIST);

  if (LOGIN_TYPE_WEBLOGIN === loginType) {
    // if (LOGIN_TYPE_WEBLOGIN !== loginType) {
    const prev = await fetchPagePreferences();

    await beAccountSettingsApiUpsertUserSettings(
      idList,
      "recipients-list-filters",
      {
        ...prev,
        status: status !== undefined ? [...status] : prev.status,
        columns: columns !== undefined ? [...columns] : prev.columns,
        modalUnsubscribe:
          modalUnsubscribe !== undefined
            ? { ...modalUnsubscribe }
            : { ...prev.modalUnsubscribe },
        modalBulkUnsubscribe:
          modalBulkUnsubscribe !== undefined
            ? { ...modalBulkUnsubscribe }
            : { ...prev.modalBulkUnsubscribe },
        groups:
          groups !== undefined
            ? {
                ...groups,
              }
            : { ...prev.groups },
      },
    );
  }
};

export const parseBoolParam = (s: string): boolean =>
  Number.parseInt(s, 10) > 0;

export const parseIntListParam = (s: string): number[] =>
  s.length > 0 ? s.split(",").map((o) => Number.parseInt(o, 10)) : [];

export const parseStringListParam = (s: string): string[] =>
  s.length > 0 ? s.split(",").map((o) => o.trim()) : [];

export const qsToSearchContact = (qsParams: PageParams): string => {
  return qsParams.se !== undefined ? qsParams.se.trim() : "";
};

export const parseDateParam = (s: string): Date | undefined => {
  const matches =
    s !== undefined && s.length > 0
      ? s.match(/^(\d{4})(?:-)?(\d{2})(?:-)?(\d{2})$/)
      : undefined;

  return Array.isArray(matches)
    ? new Date(
        Number.parseInt(matches[1], 10),
        Number.parseInt(matches[2], 10) - 1,
        Number.parseInt(matches[3], 10),
      )
    : undefined;
};

export const parseOptOutType = (t: number): SubscriptionSpecOptOutType => {
  switch (t) {
    case EMAIL_OPTOUT_TYPE_CONSOLE:
      return "OptOutPlatform";
    case EMAIL_OPTOUT_TYPE_UNSUBSCRIBE:
      return "OptOutNewsletterLink";
    case EMAIL_OPTOUT_TYPE_IMPORT:
      return "OptOutImport";
    case EMAIL_OPTOUT_TYPE_BOUNCE:
      return "OptOutBounceHB";
    case EMAIL_OPTOUT_TYPE_EMAILERRATA:
      return "OptOutBounceMailErrata";
    case EMAIL_OPTOUT_TYPE_DOMINIOERRATO:
      return "OptOutBounceDominioErrato";
    case EMAIL_OPTOUT_TYPE_CASELLAABBANDONATA:
      return "OptOutBounceCasellaAbbandonata";
    case EMAIL_OPTOUT_TYPE_FEEDBACKLOOP:
      return "OptOutFBL";
    case EMAIL_OPTOUT_TYPE_LISTUNSUBSCRIBEPAGE:
      return "OptOutUnsubHttp";
    case EMAIL_OPTOUT_TYPE_LISTUNSUBSCRIBEMAILTO:
      return "OptOutUnsubMailTo";
    case EMAIL_OPTOUT_TYPE_RECIPIENTINACTIVE:
      return "OptOutInactive";
    case EMAIL_OPTOUT_TYPE_BLOCKEDEMAIL:
      return "OptOutMailBlocked";
    case EMAIL_OPTOUT_TYPE_BLOCKEDEMAILMTA:
      return "OptOutMailBlockedMta";
    case EMAIL_OPTOUT_TYPE_BLOCKEDEMAILSPAM:
      return "OptOutMailBlockedSpam";
    case EMAIL_OPTOUT_TYPE_BLOCKEDEMAILCONFIGMTA:
      return "OptOutMailBlockedConfigMta";
    case EMAIL_OPTOUT_TYPE_CONTINUALBOUNCES:
      return "OptOutNBH";
    case EMAIL_OPTOUT_TYPE_PROVISIONALCONTBOUNCES:
      return "OptOutNBHProvisional";
    case EMAIL_OPTOUT_TYPE_DEFINITIVECONTBOUNCES:
      return "OptOutNBHDefinitive";
    case EMAIL_OPTOUT_TYPE_RIGHTTOBEFORGOTTEN:
      return "OptOutRightToBeForgotten";
  }

  return "OptOutPlatform";
};

export const qsToSearchCriteria = (qsParams: PageParams): any => {
  const filtersMode =
    qsParams.safo !== undefined && parseBoolParam(qsParams.safo) ? "Or" : "And";

  const dynFields: number[] =
    qsParams.smf !== undefined ? parseIntListParam(qsParams.smf) : [];

  const dynFieldsValues: string[] =
    qsParams.smfv !== undefined ? parseStringListParam(qsParams.smfv) : [];

  const hasDynFieldsFilters =
    dynFields.length > 0 && dynFields.length === dynFieldsValues.length;

  const dynFieldsFilters = hasDynFieldsFilters
    ? dynFields.map((idField, idx) => ({
        critType: "anagraphicField",
        identifier: `Field${idField}`,
        matchOperator: "TextSame",
        value: dynFieldsValues[idx],
      }))
    : [];

  const isChannelValid =
    qsParams.sch !== undefined && ["email", "sms"].includes(qsParams.sch);

  const isSubscriptionStatusValid =
    qsParams.ssst !== undefined &&
    ["optin", "pending", "optout"].includes(qsParams.ssst);

  return [
    qsParams.spp !== undefined
      ? {
          critType: "anagraphicField",
          identifier: "MobilePrefix",
          matchOperator: "TextContains",
          value: qsParams.spp.trim(),
        }
      : undefined,
    qsParams.spn !== undefined
      ? {
          critType: "anagraphicField",
          identifier: "MobileNumber",
          matchOperator: "TextContains",
          value: qsParams.spn.trim(),
        }
      : undefined,
    qsParams.sig !== undefined
      ? {
          critType: "groups",
          matchOperator: "BelongsTo",
          values: parseIntListParam(qsParams.sig),
        }
      : undefined,
    qsParams.sig2 !== undefined
      ? {
          critType: "groups",
          matchOperator: "BelongsTo",
          values: parseIntListParam(qsParams.sig2),
        }
      : undefined,
    qsParams.seg !== undefined
      ? {
          critType: "groups",
          matchOperator: "NotBelongsTo",
          values: parseIntListParam(qsParams.seg),
        }
      : undefined,
    qsParams.saf !== undefined
      ? {
          critType: "filters",
          mode: filtersMode,
          values: parseIntListParam(qsParams.saf),
        }
      : undefined,
    //
    ...dynFieldsFilters,
    //
    qsParams.ssf !== undefined && isChannelValid && isSubscriptionStatusValid
      ? {
          critType: qsParams.ssst === "pending" ? "pendingDate" : "optinDate",
          channel: qsParams.sch,
          datesType: "afterDate",
          dateFrom: parseDateParam(qsParams.ssf),
        }
      : undefined,

    qsParams.sst !== undefined && isChannelValid && isSubscriptionStatusValid
      ? {
          critType: qsParams.ssst === "pending" ? "pendingDate" : "optinDate",
          channel: qsParams.sch,
          datesType: "beforeDate",
          dateTo: parseDateParam(qsParams.sst),
        }
      : undefined,

    qsParams.suf !== undefined && isChannelValid
      ? {
          critType: "optoutDate",
          channel: qsParams.sch,
          datesType: "afterDate",
          dateFrom: parseDateParam(qsParams.suf),
        }
      : undefined,

    qsParams.sut !== undefined && isChannelValid
      ? {
          critType: "optoutDate",
          channel: qsParams.sch,
          datesType: "beforeDate",
          dateTo: parseDateParam(qsParams.sut),
        }
      : undefined,

    //
    qsParams.sote !== undefined
      ? {
          critType: "optoutType",
          channel: "email",
          optoutType: parseOptOutType(Number.parseInt(qsParams.sote, 10)),
        }
      : undefined,

    qsParams.sots !== undefined
      ? {
          critType: "optoutType",
          channel: "sms",
          optoutType: parseOptOutType(Number.parseInt(qsParams.sots, 10)),
        }
      : undefined,

    //
    qsParams.som !== undefined
      ? {
          critType: "optoutMessage",
          idMessage: Number.parseInt(qsParams.som, 10),
        }
      : undefined,
  ].filter((o) => o !== undefined);
};

export const qsToSearchStatus = (
  sch: string | undefined,
  ssst: string | undefined,
): SubscriptionStatus[] | undefined => {
  switch (sch) {
    case "email":
      switch (ssst) {
        case "optin":
          return ["EMAILSUBSCRIBED"];
        case "pending":
          return ["EMAILPENDING"];
        case "optout":
          return ["EMAILUNSUBSCRIBED"];
      }
      break;

    case "sms":
      switch (ssst) {
        case "optin":
          return ["MOBILESUBSCRIBED"];
        case "pending":
          return ["MOBILEPENDING"];
        case "optout":
          return ["MOBILEUNSUBSCRIBED"];
      }
      break;
  }

  return undefined;
};

export const getInitialState = async (
  qsParams: PageParams,
): Promise<{
  columns: ColumnName[];
  searchFilters: SearchFiltersState;
  modalUnsubscribePreferences: ModalUnsubscribePreferences;
  modalBulkUnsubscribePreferences: ModalBulkUnsubscribePreferences;
}> => {
  const saved = await fetchPagePreferences();

  if (Object.keys(qsParams).length === 0) {
    return {
      columns: saved.columns,
      searchFilters: {
        contact: { value: "", dirty: false },
        status: { value: saved.status, dirty: false },
        criteria: { value: [], dirty: false },
      },
      modalUnsubscribePreferences: saved.modalUnsubscribe,
      modalBulkUnsubscribePreferences: saved.modalBulkUnsubscribe,
    };
  }

  const qsStatus = qsToSearchStatus(qsParams.sch, qsParams.ssst);

  return {
    columns: saved.columns,
    searchFilters: {
      contact: { value: qsToSearchContact(qsParams), dirty: false },
      status: {
        value: qsStatus !== undefined ? qsStatus : saved.status,
        dirty: false,
      },
      criteria: { value: qsToSearchCriteria(qsParams), dirty: false },
    },
    modalUnsubscribePreferences: saved.modalUnsubscribe,
    modalBulkUnsubscribePreferences: saved.modalBulkUnsubscribe,
  };
};
